import { Validators } from '@angular/forms';
import {
  CountryCodes,
  DEFAULT_OPTIONAL_NUMBER_VALIDATORS,
  EMAIL_PATTERN,
  ListQueryParam,
  PHONE_NUMBER_PATTERN_WITH_CODE_COUNTRY,
  SSCCID_PATTERN,
} from '../../../../../common/constants';
import { ControlProperty, ListRange, SearchCriteriaBase } from '../../../../../common/models';
import { mustNotBeBlankValidator } from '../../../../../common/utils';
import { GateCheckType, NextActionApp, SealAppType, SrtScanningType } from '../constants';

export class HubBrief {
  id?: number;
  name: string;
  active: boolean;
  ssccId: string;
  inactiveCompartments: number;
  activeCompartments: number;
  assignedCompartments: number;
  country: string;
  countryDialCode: string;
  countryPlateCode: string;
  timezone: string;
  useDriverApp: boolean;
  dualSealCheckEnabled: boolean;
  departureCheckType: GateCheckType;
  departureSecurityEnabled: boolean;
  compartments: CompartmentSetting[];
  autoLoadNumberGeneration: boolean;
  fcmIntegrationEnabled = false;

  constructor(params?: HubBrief) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class CompartmentSetting {
  id: number;
  name: string;
  carrierUserPermissions?: {
    dockAllocationAllowed?: boolean;
    forceDockAllocation?: boolean;
    calendarBasedUliAllocationAllowed?: boolean;
  };

  constructor(params?: CompartmentSetting) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class HubOverview extends HubBrief {
  hubEdit: boolean;
  registrationDuration = 5;
  securityOverdueMin = 15;
  autoLoadNumberGeneration = false;
  automaticUliCleaningDaysBefore = 10;
  automaticUliCleaningDaysAfter = 10;
  automaticUliCleaningHours = 24;
  arrivalSecurityCheckEnabled = true;
  departureSecurityCheckEnabled = true;
  driverAppRequired = true;
  waitingAreaEnabled = false;
  trustedGateApp = false;
  nextActionApp: NextActionApp[] = [];
  departureCheckType: GateCheckType;
  customsSealApp: SealAppType[] = [];
  securitySealApp: SealAppType;
  dualSealCheckEnabled = false;
  coordinatorName: string;
  coordinatorEmail: string;
  coordinatorPhone: string;
  mainGatePhone: string;
  matchingRuleEnabled = false;
  twoStepCallToDockEnabled = false;
  srtScanningType: SrtScanningType;
  country: CountryCodes;
  srtOutboundEnabled = false;
  dailyTimeSlotCleanupEnabled = false;
  enteringCheckType: GateCheckType;

  constructor(params?: HubOverview) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

function validatorsByPermission(defaultValidators: Validators[], isRequiredField: boolean): Validators[] {
  return isRequiredField ? [Validators.required, ...defaultValidators] : defaultValidators;
}

export class HubOverviewForm {
  constructor(
    model: HubOverview,
    hasPermissionToCreateHub: boolean = true,
    hasPermissionToUpdateHub: boolean = true,
    public id: ControlProperty<number> = model.id,
    public ssccId: ControlProperty<string> = [
      { value: model.ssccId, disabled: !hasPermissionToCreateHub || !hasPermissionToUpdateHub },
      [Validators.required, Validators.pattern(SSCCID_PATTERN)],
    ],
    public active: ControlProperty<boolean> = model.active,
    public name: ControlProperty<string> = [
      { value: model.name, disabled: !hasPermissionToCreateHub || !hasPermissionToUpdateHub },
      [Validators.required, mustNotBeBlankValidator],
    ],
    public country: ControlProperty<CountryCodes> = [
      {
        value: model.country,
        disabled: !hasPermissionToCreateHub || !hasPermissionToUpdateHub,
      },
      [Validators.required, mustNotBeBlankValidator],
    ],
    public timezone: ControlProperty<string> = [
      { value: model.timezone, disabled: !hasPermissionToUpdateHub },
      [Validators.required],
    ],
    public registrationDuration: ControlProperty<number> = [
      { value: model.registrationDuration, disabled: !hasPermissionToUpdateHub },
      validatorsByPermission(DEFAULT_OPTIONAL_NUMBER_VALIDATORS, !hasPermissionToCreateHub),
    ],
    public securityOverdueMin: ControlProperty<number> = [
      { value: model.securityOverdueMin, disabled: !hasPermissionToUpdateHub },
      validatorsByPermission(DEFAULT_OPTIONAL_NUMBER_VALIDATORS, !hasPermissionToCreateHub),
    ],
    public automaticUliCleaningDaysBefore: ControlProperty<number> = [
      model.automaticUliCleaningDaysBefore,
      validatorsByPermission(DEFAULT_OPTIONAL_NUMBER_VALIDATORS, !hasPermissionToCreateHub),
    ],
    public automaticUliCleaningDaysAfter: ControlProperty<number> = [
      model.automaticUliCleaningDaysAfter,
      validatorsByPermission(DEFAULT_OPTIONAL_NUMBER_VALIDATORS, !hasPermissionToCreateHub),
    ],
    public arrivalSecurityCheckEnabled: ControlProperty<boolean> = [
      { value: model.arrivalSecurityCheckEnabled, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public departureSecurityCheckEnabled: ControlProperty<boolean> = [
      { value: model.departureSecurityCheckEnabled, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public waitingAreaEnabled: ControlProperty<boolean> = [
      { value: model.waitingAreaEnabled, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public twoStepCallToDockEnabled: ControlProperty<boolean> = [
      { value: model.twoStepCallToDockEnabled, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public driverAppRequired: ControlProperty<boolean> = [
      { value: model.driverAppRequired, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public srtOutboundEnabled: ControlProperty<boolean> = [
      { value: model.srtOutboundEnabled, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public srtScanningType: ControlProperty<SrtScanningType> = [
      {
        value: model.srtScanningType,
        disabled: !hasPermissionToUpdateHub,
      },
      [],
    ],
    public departureCheckType: ControlProperty<GateCheckType> = [
      { value: model.departureCheckType, disabled: !hasPermissionToUpdateHub },
      validatorsByPermission([], !hasPermissionToCreateHub),
    ],
    public enteringCheckType: ControlProperty<GateCheckType> = [
      { value: model.enteringCheckType, disabled: !hasPermissionToUpdateHub },
      validatorsByPermission([], !hasPermissionToCreateHub),
    ],
    public customsSealApp: ControlProperty<SealAppType[]> = [model.customsSealApp, []],
    public securitySealApp: ControlProperty<SealAppType> = [
      { value: model.securitySealApp, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public dualSealCheckEnabled: ControlProperty<boolean> = [
      { value: model.dualSealCheckEnabled, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public nextActionApp: ControlProperty<NextActionApp[]> = [
      { value: model.nextActionApp, disabled: !hasPermissionToUpdateHub },
      [Validators.required],
    ],
    public coordinatorName: ControlProperty<string> = model.coordinatorName,
    public coordinatorEmail: ControlProperty<string> = [model.coordinatorEmail, [Validators.pattern(EMAIL_PATTERN)]],
    public coordinatorPhone: ControlProperty<string> = [
      model.coordinatorPhone,
      [Validators.pattern(PHONE_NUMBER_PATTERN_WITH_CODE_COUNTRY)],
    ],
    public countryDialCode: ControlProperty<string> = [
      { value: model.countryDialCode, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public countryPlateCode: ControlProperty<string> = [
      { value: model.countryPlateCode, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public mainGatePhone: ControlProperty<string> = [
      { value: model.mainGatePhone, disabled: !hasPermissionToUpdateHub },
      !model.id || model.active ? [] : [Validators.required],
    ],
    public automaticUliCleaningHours: ControlProperty<number> = [
      { value: model.automaticUliCleaningHours, disabled: !hasPermissionToUpdateHub },
      validatorsByPermission([...DEFAULT_OPTIONAL_NUMBER_VALIDATORS, Validators.min(12)], !hasPermissionToCreateHub),
    ],
    public dailyTimeSlotCleanupEnabled: ControlProperty<boolean> = [
      { value: model.dailyTimeSlotCleanupEnabled, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public fcmIntegrationEnabled: ControlProperty<boolean> = [
      { value: model.fcmIntegrationEnabled, disabled: !hasPermissionToUpdateHub },
      [],
    ],
    public autoLoadNumberGeneration: ControlProperty<boolean> = [
      { value: model.autoLoadNumberGeneration, disabled: !hasPermissionToUpdateHub },
      [],
    ],
  ) {}
}

export class HubListSearchCriteria extends SearchCriteriaBase {
  [ListQueryParam.Filter]: string;
  [ListQueryParam.Size]: number;

  constructor(params?: HubListSearchCriteria | ListRange) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}
